import React, { useMemo, useState } from 'react';
import { AlertTriangle, XCircle, CheckCircle2, Zap, TrendingUp, FileText, ShieldCheck, ArrowRight, ChevronDown, ChevronRight, Info, Database, Shield } from 'lucide-react';
import { StatCard } from '../../../../shared/StatCard';
import { Badge } from '../../../../../components/ui/Badge';
import { Card } from '../../../../../components/ui/Card';
import { MODULE_CONFIG, PIPELINE_STAGES, SEVERITY_CONFIG } from '../shared';
import type { GovernanceStats, ModuleHealth, FamilyHealth, UnifiedQuestionRow, SystemStats } from '../hooks/useGovernanceData';
import type { IssueDomain } from '../../../../../governance';
import { getGuardGovernanceIssues } from '../../../../../practice/logic/guardGovernanceBridge';
interface OverviewDashboardProps {
  stats: GovernanceStats;
  systemStats: SystemStats;
  moduleHealth: ModuleHealth[];
  familyHealthByModule: Record<string, FamilyHealth[]>;
  questionTypeDistribution: Map<string, number>;
  visualTypeDistribution: Map<string, number>;
  topBlockers: UnifiedQuestionRow[];
  pipelineIssueCounts: Record<IssueDomain, number> | null;
  isRunning: boolean;
  onNavigateToQuestion: (filter?: string) => void;
}
export function OverviewDashboard({
  stats,
  systemStats,
  moduleHealth,
  familyHealthByModule,
  questionTypeDistribution,
  visualTypeDistribution,
  topBlockers,
  pipelineIssueCounts,
  isRunning,
  onNavigateToQuestion
}: OverviewDashboardProps) {
  const isHealthy = stats.fail === 0 && stats.review === 0;
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  // P-9: Guard → Governance feedback data
  const guardSummary = useMemo(() => getGuardGovernanceIssues(), []);
  const toggleModule = (code: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);else next.add(code);
      return next;
    });
  };
  // Total findings across all pipeline stages
  const totalFindings = pipelineIssueCounts ? Object.values(pipelineIssueCounts).reduce((sum, c) => sum + c, 0) : 0;
  return <div className="space-y-6" data-id="element-4233">
      {/* Health Banner — question-level summary */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isHealthy ? 'bg-emerald-50 border-emerald-200' : stats.fail > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-4234">
        
        {isHealthy ? <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-4235" /> : stats.fail > 0 ? <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-4236" /> : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" data-id="element-4237" />}
        <div className="flex-1 min-w-0" data-id="element-4238">
          <span className={`text-sm font-semibold ${isHealthy ? 'text-emerald-800' : stats.fail > 0 ? 'text-red-800' : 'text-amber-800'}`} data-id="element-4239">
            
            {isHealthy ? 'Alle vragen voldoen aan de kwaliteitseisen' : stats.fail > 0 ? `${stats.fail} ${stats.fail === 1 ? 'vraag' : 'vragen'} afgekeurd` : 'Aandachtspunten gevonden'}
          </span>
          {!isHealthy && <span className={`text-xs ml-2 ${stats.fail > 0 ? 'text-red-700' : 'text-amber-700'}`} data-id="element-4240">
            
              {[stats.review > 0 && `${stats.review} vereisen review`, totalFindings > 0 && `${totalFindings} bevindingen totaal`].filter(Boolean).join(' · ')}
            </span>}
        </div>
      </div>

      {/* Stats — question-level counts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4241">
        <StatCard label="Totaal Vragen" value={stats.total.toLocaleString('nl-NL')} icon={<FileText className="w-5 h-5" data-id="element-4243" />} color="blue" subtitle="Unieke vragen in het systeem" data-id="element-4242" />
        
        <StatCard label="Geslaagd (PASS)" value={stats.pass.toLocaleString('nl-NL')} icon={<CheckCircle2 className="w-5 h-5" data-id="element-4245" />} color="emerald" subtitle={`${Math.round(stats.pass / Math.max(stats.total, 1) * 100)}% van alle vragen`} data-id="element-4244" />
        
        <StatCard label="Afgekeurd (FAIL)" value={stats.fail.toLocaleString('nl-NL')} icon={<XCircle className="w-5 h-5" data-id="element-4247" />} color={stats.fail === 0 ? 'emerald' : 'red'} subtitle={`${stats.blockerCount} blocker-bevindingen`} data-id="element-4246" />
        
        <StatCard label="Review Nodig" value={stats.review.toLocaleString('nl-NL')} icon={<AlertTriangle className="w-5 h-5" data-id="element-4249" />} color={stats.review === 0 ? 'emerald' : 'amber'} subtitle={`${stats.warningCount} verbeterpunten`} data-id="element-4248" />
        
      </div>

      {/* Explainer legend */}
      <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-blue-50/50 border border-blue-100" data-id="element-4250">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-4251" />
        <div className="text-xs text-blue-800 leading-relaxed" data-id="element-4252">
          <span className="font-semibold" data-id="element-4253">Hoe lees je deze cijfers?</span>{' '}
          <span className="font-semibold text-red-700" data-id="element-4254">Afgekeurd</span> en{' '}
          <span className="font-semibold text-amber-700" data-id="element-4255">Review</span> tellen
          het aantal <em data-id="element-4256">vragen</em> met die status. Elke vraag kan meerdere{' '}
          <em data-id="element-4257">bevindingen</em> hebben (blocker = blokkerend, verbeterpunt =
          advies). De Pipeline Fasen hieronder tonen het totaal aantal
          bevindingen per controle-fase.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4258">
        {/* Left Column: Top Blockers & Pipeline Stages & System Coverage */}
        <div className="lg:col-span-2 space-y-6" data-id="element-4259">
          {/* System Coverage */}
          <Card className="p-5" data-id="element-4260">
            <div className="mb-4" data-id="element-4261">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4262">
                <Database className="w-4 h-4 text-indigo-500" data-id="element-4263" /> Systeemdekking
              </h3>
              <p className="text-[11px] text-gray-500 mt-1" data-id="element-4264">
                Overzicht van beschikbare componenten en hun gebruik in het
                vragenbestand.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-id="element-4265">
              <div className="bg-indigo-50/50 rounded-lg p-3 border border-indigo-100 text-center" data-id="element-4266">
                <p className="text-2xl font-bold text-indigo-700" data-id="element-4267">
                  {systemStats.visualTypeCount}
                </p>
                <p className="text-xs font-medium text-indigo-600 mt-1" data-id="element-4268">
                  Visual Types
                </p>
              </div>
              <div className="bg-purple-50/50 rounded-lg p-3 border border-purple-100 text-center" data-id="element-4269">
                <p className="text-2xl font-bold text-purple-700" data-id="element-4270">
                  {systemStats.questionTypeCount}
                </p>
                <p className="text-xs font-medium text-purple-600 mt-1" data-id="element-4271">
                  Vraagtypes
                </p>
              </div>
              <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100 text-center" data-id="element-4272">
                <p className="text-2xl font-bold text-blue-700" data-id="element-4273">
                  {systemStats.familyCount}
                </p>
                <p className="text-xs font-medium text-blue-600 mt-1" data-id="element-4274">
                  Families
                </p>
              </div>
              <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100 text-center" data-id="element-4275">
                <p className="text-2xl font-bold text-emerald-700" data-id="element-4276">
                  {systemStats.moduleCount}
                </p>
                <p className="text-xs font-medium text-emerald-600 mt-1" data-id="element-4277">
                  Modules
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-4278">
              <div data-id="element-4279">
                <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider" data-id="element-4280">
                  Top 5 Vraagtypes
                </h4>
                <div className="space-y-2" data-id="element-4281">
                  {Array.from(questionTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm" data-id="element-4282">
                    
                        <span className="text-gray-600 font-mono text-xs truncate mr-2" data-id="element-4283">
                          {type}
                        </span>
                        <span className="font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-xs" data-id="element-4284">
                          {count}
                        </span>
                      </div>)}
                </div>
              </div>
              <div data-id="element-4285">
                <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider" data-id="element-4286">
                  Top 5 Visuals
                </h4>
                <div className="space-y-2" data-id="element-4287">
                  {Array.from(visualTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm" data-id="element-4288">
                    
                        <span className="text-gray-600 font-mono text-xs truncate mr-2" data-id="element-4289">
                          {type}
                        </span>
                        <span className="font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-xs" data-id="element-4290">
                          {count}
                        </span>
                      </div>)}
                </div>
              </div>
            </div>
          </Card>

          {/* Top Blockers */}
          <Card className="p-5" data-id="element-4291">
            <div className="flex items-center justify-between mb-4" data-id="element-4292">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4293">
                <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-4294" /> Afgekeurde
                Vragen
                {topBlockers.length > 0 && <span className="text-xs font-normal text-gray-500" data-id="element-4295">
                    (top {topBlockers.length})
                  </span>}
              </h3>
              {topBlockers.length > 0 && <button onClick={() => onNavigateToQuestion('blockers')} className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-4296">
                
                  Bekijk alle {stats.fail} <ArrowRight className="w-3 h-3" data-id="element-4297" />
                </button>}
            </div>

            {topBlockers.length === 0 ? <div className="bg-gray-50 rounded-lg p-8 text-center border border-gray-100" data-id="element-4298">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-4299" />
                <p className="text-sm font-medium text-gray-700" data-id="element-4300">
                  Geen afgekeurde vragen!
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-4301">
                  Alle vragen komen door de pipeline.
                </p>
              </div> : <div className="space-y-2" data-id="element-4302">
                {topBlockers.map(row => {
              const firstBlocker = row.pipelineIssues.find(i => i.kind === 'blocker');
              const modConfig = MODULE_CONFIG[row.moduleCode] || {
                bgClass: 'bg-gray-500'
              };
              return <div key={row.questionId} className="flex items-start gap-3 p-3 rounded-lg border border-red-100 bg-red-50/50 hover:bg-red-50 transition-colors cursor-pointer" onClick={() => onNavigateToQuestion(row.questionId)} data-id="element-4303">
                    
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${modConfig.bgClass}`} data-id="element-4304" />
                    
                      <div className="min-w-0 flex-1" data-id="element-4305">
                        <div className="flex items-center gap-2 mb-1" data-id="element-4306">
                          <span className="font-mono text-xs font-bold text-gray-900" data-id="element-4307">
                            {row.questionId}
                          </span>
                          <Badge variant="danger" size="sm" data-id="element-4308">
                            FAIL
                          </Badge>
                          <span className="text-[10px] text-gray-500" data-id="element-4309">
                            {row.blockerCount} blocker
                            {row.blockerCount !== 1 ? 's' : ''}
                            {row.warningCount > 0 && ` · ${row.warningCount} verbeterpunt${row.warningCount !== 1 ? 'en' : ''}`}
                          </span>
                        </div>
                        {firstBlocker && <p className="text-sm text-red-800 truncate" data-id="element-4310">
                            <span className="font-semibold text-xs uppercase mr-1" data-id="element-4311">
                              [{firstBlocker.domain}]
                            </span>
                            {firstBlocker.message}
                          </p>}
                      </div>
                    </div>;
            })}
              </div>}
          </Card>

          {/* Pipeline Stages — individual findings per stage */}
          <Card className="p-5" data-id="element-4312">
            <div className="mb-4" data-id="element-4313">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4314">
                <Zap className="w-4 h-4 text-amber-500" data-id="element-4315" /> Pipeline Fasen
              </h3>
              <p className="text-[11px] text-gray-500 mt-1" data-id="element-4316">
                Aantal individuele bevindingen per controle-fase (één vraag kan
                meerdere bevindingen opleveren).
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4317">
              {PIPELINE_STAGES.map(stage => {
              const count = pipelineIssueCounts?.[stage.domain] ?? 0;
              const status = !pipelineIssueCounts ? 'pending' : count === 0 ? 'ok' : count <= 5 ? 'warning' : 'error';
              const statusConfig = {
                ok: {
                  dot: 'bg-emerald-500',
                  bg: 'bg-emerald-50'
                },
                warning: {
                  dot: 'bg-amber-500',
                  bg: 'bg-amber-50'
                },
                error: {
                  dot: 'bg-red-500',
                  bg: 'bg-red-50'
                },
                pending: {
                  dot: 'bg-gray-300',
                  bg: 'bg-gray-50'
                }
              }[status];
              return <div key={stage.name} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50/50" data-id="element-4318">
                    
                    <div className="flex items-center gap-3" data-id="element-4319">
                      <div className={`w-2 h-2 rounded-full ${statusConfig.dot}`} data-id="element-4320" />
                      
                      <span className="text-sm font-medium text-gray-700" data-id="element-4321">
                        {stage.label}
                      </span>
                    </div>
                    {status !== 'pending' && <Badge variant={status === 'ok' ? 'success' : status === 'warning' ? 'warning' : 'danger'} size="sm" data-id="element-4322">
                      
                        {count === 0 ? '0 bevindingen' : `${count} bevindingen`}
                      </Badge>}
                  </div>;
            })}
            </div>
          </Card>
        </div>

        {/* Right Column: Module Health */}
        <div className="space-y-6" data-id="element-4323">
          <Card className="p-5" data-id="element-4324">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-4325">
              <FileText className="w-4 h-4 text-blue-500" data-id="element-4326" /> Module Gezondheid
            </h3>
            <div className="space-y-3" data-id="element-4327">
              {moduleHealth.map(mod => {
              const config = MODULE_CONFIG[mod.code];
              const bgClass = config?.bgClass ?? 'bg-gray-400';
              const isExpanded = expandedModules.has(mod.code);
              const families = familyHealthByModule[mod.code] || [];
              return <div key={mod.code} className="space-y-1.5 border border-gray-100 rounded-lg p-2 bg-white" data-id="element-4328">
                    
                    <div className="flex items-center justify-between text-xs cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors" onClick={() => toggleModule(mod.code)} data-id="element-4329">
                      
                      <div className="flex items-center gap-1.5 font-medium text-gray-700" data-id="element-4330">
                        {isExpanded ? <ChevronDown className="w-3 h-3 text-gray-400" data-id="element-4331" /> : <ChevronRight className="w-3 h-3 text-gray-400" data-id="element-4332" />}
                        <div className={`w-1.5 h-1.5 rounded-full ${bgClass}`} data-id="element-4333" />
                        
                        {mod.code}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-4334">
                        <span className="text-gray-500" data-id="element-4335">{mod.total} vr</span>
                        <span className={`font-bold ${mod.pct === 100 ? 'text-emerald-600' : mod.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-4336">
                          
                          {mod.pct}%
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mx-1" data-id="element-4337">
                      <div className={`h-full rounded-full ${mod.pct === 100 ? 'bg-emerald-500' : mod.pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${mod.pct}%`
                  }} data-id="element-4338" />
                      
                    </div>

                    {isExpanded && families.length > 0 && <div className="mt-2 pt-2 border-t border-gray-100 space-y-2 pl-4 pr-1" data-id="element-4339">
                        {families.map(fam => <div key={fam.familyId} className="space-y-1" data-id="element-4340">
                            <div className="flex items-center justify-between text-[10px]" data-id="element-4341">
                              <span className="font-mono text-gray-600 truncate mr-2" title={fam.familyId} data-id="element-4342">
                            
                                {fam.familyId}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0" data-id="element-4343">
                                <span className="text-gray-400" data-id="element-4344">
                                  {fam.pass}/{fam.total}
                                </span>
                                <span className={`font-semibold ${fam.pct === 100 ? 'text-emerald-600' : fam.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-4345">
                              
                                  {fam.pct}%
                                </span>
                              </div>
                            </div>
                            <div className="h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-4346">
                              <div className={`h-full rounded-full ${fam.pct === 100 ? 'bg-emerald-400' : fam.pct >= 80 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                        width: `${fam.pct}%`
                      }} data-id="element-4347" />
                          
                            </div>
                          </div>)}
                      </div>}
                  </div>;
            })}
            </div>
          </Card>

          {/* P-9: Guard Runtime Rejections */}
          <Card className="p-5" data-id="element-4348">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2" data-id="element-4349">
              <Shield className="w-4 h-4 text-orange-500" data-id="element-4350" /> Runtime Guard
            </h3>
            <p className="text-[11px] text-gray-500 mb-3" data-id="element-4351">
              Vragen die door de pedagogische guard worden afgewezen tijdens
              oefensessies.
            </p>
            {guardSummary.totalRejections === 0 ? <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-100" data-id="element-4352">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4353" />
                <p className="text-xs text-gray-600" data-id="element-4354">
                  Geen runtime afwijzingen
                </p>
              </div> : <div className="space-y-3" data-id="element-4355">
                <div className="grid grid-cols-2 gap-2" data-id="element-4356">
                  <div className="bg-red-50 rounded-lg p-2.5 border border-red-100 text-center" data-id="element-4357">
                    <p className="text-lg font-bold text-red-700" data-id="element-4358">
                      {guardSummary.totalAffectedQuestions}
                    </p>
                    <p className="text-[10px] font-medium text-red-600" data-id="element-4359">
                      Vragen
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-2.5 border border-orange-100 text-center" data-id="element-4360">
                    <p className="text-lg font-bold text-orange-700" data-id="element-4361">
                      {guardSummary.totalRejections}
                    </p>
                    <p className="text-[10px] font-medium text-orange-600" data-id="element-4362">
                      Afwijzingen
                    </p>
                  </div>
                </div>
                {guardSummary.reasonBreakdown.length > 0 && <div className="space-y-1.5" data-id="element-4363">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-4364">
                      Top redenen
                    </p>
                    {guardSummary.reasonBreakdown.slice(0, 3).map(r => <div key={r.reason} className="flex items-start gap-2 text-[11px]" data-id="element-4365">
                  
                        <span className="font-mono text-red-600 font-semibold flex-shrink-0" data-id="element-4366">
                          {r.count}×
                        </span>
                        <span className="text-gray-600 leading-tight" data-id="element-4367">
                          {r.description}
                        </span>
                      </div>)}
                  </div>}
                {guardSummary.issues.length > 0 && <div className="space-y-1.5" data-id="element-4368">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-4369">
                      Meest afgewezen
                    </p>
                    {guardSummary.issues.slice(0, 3).map(issue => <div key={issue.questionId} className="flex items-center justify-between text-[10px]" data-id="element-4370">
                  
                        <span className="font-mono text-gray-700 truncate mr-2" title={issue.questionId} data-id="element-4371">
                    
                          {issue.questionId}
                        </span>
                        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-4372">
                          {issue.fatalCount > 0 && <span className="text-red-600 font-semibold" data-id="element-4373">
                              {issue.fatalCount}F
                            </span>}
                          {issue.softCount > 0 && <span className="text-amber-600 font-semibold" data-id="element-4374">
                              {issue.softCount}S
                            </span>}
                        </div>
                      </div>)}
                  </div>}
                {guardSummary.dataRange && <p className="text-[10px] text-gray-400 pt-1 border-t border-gray-100" data-id="element-4375">
                    Data:{' '}
                    {new Date(guardSummary.dataRange.from).toLocaleDateString('nl-NL')}{' '}
                    —{' '}
                    {new Date(guardSummary.dataRange.to).toLocaleDateString('nl-NL')}
                  </p>}
              </div>}
          </Card>
        </div>
      </div>
    </div>;
}